// /home/labdoo/zitech-nexus/frontend/src/pages/admin/UserDetail.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminAPI, getRoleDisplayName, getRoleBadgeColor } from '../../services/api';
import './UserDetail.css';

// SVG Icons
const Icons = {
  Back: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  ),
  Edit: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 3L21 7L7 21H3V17L17 3Z" />
    </svg>
  ),
  User: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  Email: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  Phone: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  ),
  Company: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  ),
  Calendar: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  Role: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L2 7L12 12L22 7L12 2Z" />
      <path d="M2 17L12 22L22 17" />
      <path d="M2 12L12 17L22 12" />
    </svg>
  ),
  Status: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8V12M12 16H12.01" />
    </svg>
  ),
  Spinner: () => (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#0d47a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ud-spinner-svg">
      <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
  StatusDot: () => (
    <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor">
      <circle cx="4" cy="4" r="4" />
    </svg>
  ),
};

const UserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);

  const fetchUser = useCallback(async () => {
    try {
      const response = await adminAPI.getUser(parseInt(id!));
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const handleStatusToggle = async () => {
    try {
      await adminAPI.updateUser(parseInt(id!), { is_active: !user.is_active });
      fetchUser();
    } catch (error) {
      console.error('Error updating user status:', error);
      alert('Failed to update user status');
    }
  };

  const getRoleColor = (role: string) => {
    switch(role) {
      case 'superadmin': return '#8b5cf6';
      case 'admin': return '#0ea5e9';
      case 'user': return '#10b981';
      case 'trainee': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const getRoleBackground = (role: string) => {
    switch(role) {
      case 'superadmin': return '#ede9fe';
      case 'admin': return '#dbeafe';
      case 'user': return '#d1fae5';
      case 'trainee': return '#fef3c7';
      default: return '#f3f4f6';
    }
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? '#10b981' : '#ef4444';
  };

  const getStatusBackground = (isActive: boolean) => {
    return isActive ? '#d1fae5' : '#fee2e2';
  };

  if (loading) {
    return (
      <div className="ud-loading-container">
        <div className="ud-spinner">
          <Icons.Spinner />
        </div>
        <p className="ud-loading-text">Loading user details...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="ud-error-container">
        <div className="ud-error-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <h2 className="ud-error-title">User Not Found</h2>
        <p className="ud-error-text">The user you're looking for doesn't exist or has been removed.</p>
        <button className="ud-error-btn" onClick={() => navigate('/dashboard/admin/users')}>
          <Icons.Back />
          Back to Users
        </button>
      </div>
    );
  }

  return (
    <div className="ud-container">
      {/* Header */}
      <div className="ud-header">
        <div className="ud-header-left">
          <button className="ud-back-btn" onClick={() => navigate('/dashboard/admin/users')}>
            <Icons.Back />
            Back to Users
          </button>
          <h1 className="ud-title">User Details</h1>
          <div className="ud-meta">
            <span className="ud-meta-item">
              <Icons.User />
              {user.full_name || user.username}
            </span>
            <span className="ud-meta-divider">|</span>
            <span className="ud-meta-item">
              <Icons.Email />
              {user.email}
            </span>
            <span className="ud-meta-divider">|</span>
            <span className="ud-meta-item">
              <Icons.Calendar />
              Member since {new Date(user.date_joined).toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric', 
                year: 'numeric' 
              })}
            </span>
          </div>
        </div>
        <div className="ud-header-actions">
          <div className="ud-header-status">
            <span 
              className="ud-status-badge-large"
              style={{
                background: getStatusBackground(user.is_active),
                color: getStatusColor(user.is_active)
              }}
            >
              <Icons.StatusDot />
              {user.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
          <button 
            className="ud-btn-primary"
            onClick={() => navigate(`/dashboard/admin/users/${id}/edit`)}
          >
            <Icons.Edit />
            Edit User
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="ud-stats-bar">
        <div className="ud-stat-item">
          <span className="ud-stat-label">Role</span>
          <span className="ud-stat-value">
            <span 
              className="ud-role-badge"
              style={{
                background: getRoleBackground(user.role),
                color: getRoleColor(user.role)
              }}
            >
              {getRoleDisplayName(user.role)}
            </span>
          </span>
        </div>
        <div className="ud-stat-divider"></div>
        <div className="ud-stat-item">
          <span className="ud-stat-label">Status</span>
          <span className="ud-stat-value">
            <button 
              className={`ud-status-toggle ${user.is_active ? 'ud-status-toggle-active' : 'ud-status-toggle-inactive'}`}
              onClick={handleStatusToggle}
            >
              {user.is_active ? 'Deactivate' : 'Activate'}
            </button>
          </span>
        </div>
        <div className="ud-stat-divider"></div>
        <div className="ud-stat-item">
          <span className="ud-stat-label">Last Login</span>
          <span className="ud-stat-value ud-stat-login">
            <Icons.Calendar />
            {user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
          </span>
        </div>
      </div>

      {/* Detail Grid */}
      <div className="ud-detail-grid">
        {/* Left Column - Personal Information */}
        <div className="ud-card">
          <h3 className="ud-card-title">
            <Icons.User />
            Personal Information
          </h3>
          <div className="ud-info-row">
            <span className="ud-info-label">
              <Icons.User />
              Full Name
            </span>
            <span className="ud-info-value">{user.full_name || user.username}</span>
          </div>
          <div className="ud-info-row">
            <span className="ud-info-label">
              <Icons.Email />
              Email
            </span>
            <span className="ud-info-value">
              <a href={`mailto:${user.email}`} className="ud-email-link">
                {user.email}
              </a>
            </span>
          </div>
          <div className="ud-info-row">
            <span className="ud-info-label">
              <Icons.Phone />
              Phone Number
            </span>
            <span className="ud-info-value">{user.phone_number || 'Not provided'}</span>
          </div>
          <div className="ud-info-row">
            <span className="ud-info-label">
              <Icons.Company />
              Company
            </span>
            <span className="ud-info-value">{user.company_name || 'Not provided'}</span>
          </div>
        </div>

        {/* Right Column - Account Information */}
        <div className="ud-card">
          <h3 className="ud-card-title">
            <Icons.Role />
            Account Information
          </h3>
          <div className="ud-info-row">
            <span className="ud-info-label">
              <Icons.Role />
              Role
            </span>
            <span className="ud-info-value">
              <span 
                className="ud-role-badge"
                style={{
                  background: getRoleBackground(user.role),
                  color: getRoleColor(user.role)
                }}
              >
                {getRoleDisplayName(user.role)}
              </span>
            </span>
          </div>
          <div className="ud-info-row">
            <span className="ud-info-label">
              <Icons.Status />
              Status
            </span>
            <span className="ud-info-value">
              <span 
                className="ud-status-badge"
                style={{
                  background: getStatusBackground(user.is_active),
                  color: getStatusColor(user.is_active)
                }}
              >
                <Icons.StatusDot />
                {user.is_active ? 'Active' : 'Inactive'}
              </span>
              <button 
                className={`ud-toggle-btn ${user.is_active ? 'ud-toggle-deactivate' : 'ud-toggle-activate'}`}
                onClick={handleStatusToggle}
              >
                {user.is_active ? 'Deactivate' : 'Activate'}
              </button>
            </span>
          </div>
          <div className="ud-info-row">
            <span className="ud-info-label">
              <Icons.Calendar />
              Member Since
            </span>
            <span className="ud-info-value">
              {new Date(user.date_joined).toLocaleDateString('en-US', { 
                weekday: 'long',
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </span>
          </div>
          <div className="ud-info-row">
            <span className="ud-info-label">
              <Icons.Calendar />
              Last Login
            </span>
            <span className="ud-info-value">
              {user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
            </span>
          </div>
          <div className="ud-info-row">
            <span className="ud-info-label">User ID</span>
            <span className="ud-info-value ud-user-id">#{user.id}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetail;